import { useAuthStore } from '@/stores/auth';
import { storedOwner, tokenOwner } from '@/lib/api';
import { getAccessToken } from '@/stores/auth';
import { offlineDb } from './db';

let storageError: string | null = null;
let channel: BroadcastChannel | null = null;
export function connectOfflineChannel() {
  if (!channel && typeof window.BroadcastChannel === 'function') {
    channel = new window.BroadcastChannel('camadero-offline-parts');
    channel.onmessage = () => window.dispatchEvent(new Event('offline:change'));
  }
}
export function getStorageError() {
  return storageError;
}
export function notifyOfflineChange() {
  window.dispatchEvent(new Event('offline:change'));
  channel?.postMessage('change');
}
export function reportStorageError(error: unknown) {
  const message = `No se pudo guardar en este dispositivo: ${error instanceof Error ? error.message : 'almacenamiento no disponible'}`;
  if (storageError !== message) {
    storageError = message;
    notifyOfflineChange();
  }
}
export function reportStorageFailure(error: unknown) {
  const name = (error as { name?: string } | null)?.name;
  if (
    error instanceof DOMException ||
    [
      'QuotaExceededError',
      'SecurityError',
      'UnknownError',
      'DatabaseClosedError',
      'MissingAPIError',
      'OpenFailedError',
      'UpgradeError',
      'InvalidStateError',
      'AbortError',
    ].includes(name ?? '')
  )
    reportStorageError(error);
}
export function clearStorageError() {
  if (storageError !== null) {
    storageError = null;
    notifyOfflineChange();
  }
}
export function currentOwner() {
  const id = useAuthStore.getState().user?.id ?? null;
  return id !== null && storedOwner() === id && tokenOwner(getAccessToken()) === id ? id : null;
}
export function snapshotId(key: readonly unknown[], ownerId: number) {
  return `${ownerId}:${JSON.stringify(key)}`;
}
export async function cacheGeneration(ownerId: number | null) {
  return ownerId == null || !offlineDb.bundles
    ? undefined
    : (await offlineDb.bundles.get(String(ownerId)))?.generation;
}
export function canUseOfflineCache(key: readonly unknown[]) {
  return [
    'camadas',
    'lotes-nave',
    'naves',
    'registros-diarios',
    'entregas-pienso',
    'pesajes',
    'salidas',
    'catalogos',
    'liquidacion',
    'liquidacion-preview',
    'liquidacion-evolucion',
    'iot',
    'api-path',
  ].includes(String(key[0]));
}
export function isTemporaryFailure(error: unknown) {
  const status = (error as { status?: number } | null)?.status;
  return typeof status === 'number'
    ? status >= 500
    : error instanceof TypeError ||
        (error instanceof DOMException && error.name === 'NetworkError');
}
export async function getSnapshot<T>(
  key: readonly unknown[],
  ownerId = currentOwner(),
): Promise<T | undefined> {
  if (ownerId == null || !canUseOfflineCache(key)) return undefined;
  const row = await offlineDb.snapshots.get(snapshotId(key, ownerId));
  if (row && row.generation !== (await cacheGeneration(ownerId))) return undefined;
  return row?.data as T | undefined;
}
export async function saveSnapshot(
  key: readonly unknown[],
  data: unknown,
  ownerId = currentOwner(),
  expectedGeneration?: string | null,
): Promise<void> {
  if (ownerId == null || !canUseOfflineCache(key)) return;
  const persist = async () => {
    const generation = await cacheGeneration(ownerId);
    if (expectedGeneration !== undefined && expectedGeneration !== (generation ?? null))
      throw Object.assign(new Error('La copia local ha cambiado. Vuelve a consultar los datos.'), {
        status: 409,
      });
    if (currentOwner() !== ownerId)
      throw Object.assign(new Error('La sesión ha cambiado.'), { status: 401 });
    await offlineDb.snapshots.put({
      id: snapshotId(key, ownerId),
      ownerId,
      key: JSON.stringify(key),
      data,
      savedAt: Date.now(),
      generation,
    });
  };
  if (offlineDb.bundles)
    await offlineDb.transaction('rw', offlineDb.snapshots, offlineDb.bundles, persist);
  else await persist();
}
export async function cacheReadThrough<T>(
  key: readonly unknown[],
  loader: () => Promise<T>,
): Promise<T> {
  const ownerId = currentOwner();
  let generation: string | null | undefined;
  try {
    generation = (await cacheGeneration(ownerId)) ?? null;
  } catch (error) {
    reportStorageFailure(error);
  }
  let result: T;
  try {
    result = await loader();
  } catch (error) {
    if (!isTemporaryFailure(error) || ownerId == null || currentOwner() !== ownerId) throw error;
    try {
      const cached = await getSnapshot<T>(key, ownerId);
      if (cached !== undefined) return cached;
    } catch (storageFailure) {
      reportStorageError(storageFailure);
    }
    throw error;
  }
  if (currentOwner() !== ownerId) {
    const error = Object.assign(
      new Error('La sesión ha cambiado. Recarga los datos con tu usuario.'),
      { status: 401 },
    );
    throw error;
  }
  try {
    await saveSnapshot(key, result, ownerId, generation);
  } catch (error) {
    if ((error as { status?: number })?.status) throw error;
    reportStorageError(error);
  }
  return result;
}
